import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, Clock, ArrowsClockwise, TrendUp, TrendDown, House, Bank, Percent
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

function ChangeIndicator({ value, suffix = "" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-red-400" : "text-emerald-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(3)}{suffix}
    </span>
  );
}

export default function TreasuryReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState("6m");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/treasury-10y`);
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Treasury data");
    } finally {
      setLoading(false);
    }
  };

  const current = data?.current || {};
  const chartData = chartView === "5y" ? (data?.weekly || []) : (data?.history || []);
  const yieldVal = current.yield_pct;
  const yieldColor = yieldVal >= 5 ? "#ef4444" : yieldVal >= 4 ? "#f59e0b" : "#10b981";

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="treasury-report">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">US 10-Year Treasury Yield</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
            <Bank size={12} />
            United States Government Bond benchmark yield
          </p>
        </div>
        <button onClick={fetchData} disabled={loading} data-testid="treasury-refresh-btn"
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm">
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="treasury-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchData} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          <Skeleton className="h-[140px] bg-zinc-900/50" />
          <Skeleton className="h-[250px] bg-zinc-900/50" />
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Current yield hero */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="treasury-stats">
            <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-4 col-span-2 md:col-span-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Current Yield</p>
              <p className="text-3xl font-data font-bold" style={{ color: yieldColor }}>{yieldVal?.toFixed(3)}%</p>
              <p className="text-[10px] text-zinc-600 mt-1">{current.date}</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Day Change</p>
              <ChangeIndicator value={current.day_change} suffix="%" />
              <p className="text-[9px] text-zinc-600 mt-1">({current.day_change_pct > 0 ? "+" : ""}{current.day_change_pct?.toFixed(2)}%)</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Previous Close</p>
              <p className="text-sm font-data font-bold text-zinc-200">{current.prev_close?.toFixed(3)}%</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">6-Month High</p>
              <p className="text-sm font-data font-bold text-red-400">{current.high_6m?.toFixed(3)}%</p>
            </div>
            <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">6-Month Low</p>
              <p className="text-sm font-data font-bold text-emerald-400">{current.low_6m?.toFixed(3)}%</p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5" data-testid="treasury-chart-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-medium text-zinc-300">Yield History</h2>
              <Tabs value={chartView} onValueChange={setChartView}>
                <TabsList className="h-7 bg-zinc-800/50 p-0.5">
                  <TabsTrigger value="6m" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="treasury-chart-6m">6 Months</TabsTrigger>
                  <TabsTrigger value="5y" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="treasury-chart-5y">5 Years</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <CpiChart data={chartData} color={yieldColor} height={220} />
          </div>

          {/* Mortgage rate explanation */}
          <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm overflow-hidden" data-testid="treasury-mortgage-info">
            <div className="p-5 border-b border-zinc-800/50">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-sm bg-blue-900/20 flex items-center justify-center">
                  <House size={16} weight="bold" className="text-blue-400" />
                </div>
                <h2 className="text-sm font-heading font-bold text-zinc-100">How the 10-Year Treasury Affects Mortgage Rates</h2>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                The 10-year Treasury yield is the single most important benchmark for U.S. mortgage rates. When you get a 30-year fixed mortgage, the rate you pay is heavily influenced by this number. Here's how it works:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-800/50">
              <div className="p-5 space-y-3">
                <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">The Connection</h3>
                <ul className="space-y-2.5">
                  <li className="flex items-start gap-2">
                    <TrendUp size={11} weight="bold" className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed"><strong className="text-zinc-100">Banks use Treasury yields as their baseline.</strong> When lenders set mortgage rates, they start with the 10-year Treasury yield and add a "spread" (typically 1.5% to 2.5%) on top. This spread covers their risk and profit.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendUp size={11} weight="bold" className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed"><strong className="text-zinc-100">When Treasury yields rise, mortgage rates rise.</strong> If the 10-year yield goes from 4.0% to 4.5%, expect mortgage rates to jump by roughly the same amount within days to weeks.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendDown size={11} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed"><strong className="text-zinc-100">When Treasury yields fall, mortgage rates fall.</strong> This is why homebuyers watch this number — a dropping yield means cheaper mortgages are likely coming.</span>
                  </li>
                </ul>
              </div>
              <div className="p-5 space-y-3">
                <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Why the 10-Year Specifically?</h3>
                <ul className="space-y-2.5">
                  <li className="flex items-start gap-2">
                    <Percent size={11} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed">Even though mortgages are 30-year loans, most people sell or refinance within 7-10 years. This makes the 10-year Treasury the best match for the actual life of a mortgage.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Bank size={11} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed">Mortgage-backed securities (MBS) — the bonds that fund your mortgage — compete with Treasuries for investors. If Treasuries pay more, MBS rates must rise to stay attractive.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendUp size={11} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed">The Federal Reserve doesn't directly set mortgage rates — they set the federal funds rate (short-term). The 10-year yield is set by the bond market based on expectations about inflation, economic growth, and Fed policy.</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="p-4 bg-zinc-900/60 border-t border-zinc-800/50">
              <p className="text-[10px] text-zinc-500 leading-relaxed">
                <strong className="text-zinc-400">Quick rule of thumb:</strong> Your mortgage rate is roughly the 10-year Treasury yield + 1.5% to 2.5%. 
                With the current yield at <strong className="text-zinc-300">{yieldVal?.toFixed(2)}%</strong>, expect 30-year fixed mortgage rates around <strong className="text-zinc-300">{(yieldVal + 1.7)?.toFixed(1)}% to {(yieldVal + 2.3)?.toFixed(1)}%</strong>.
              </p>
            </div>
          </div>
        </>
      )}

      {data && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50" data-testid="treasury-footer">
          <p className="text-[10px] text-zinc-600">Source: {data.source}</p>
          <div className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock size={10} />
            <span>Updated: {new Date(data.fetched_at).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
